import { differenceInCalendarDays, formatDistanceToNow, parseISO } from 'date-fns';
import type { HealthStatus } from '@/types/starter';

/**
 * Calendar days since last feeding (not 24-hour periods).
 * Uses differenceInCalendarDays for DST safety and user mental model.
 */
export function getDaysSinceFeeding(lastFedDate: string): number {
  return differenceInCalendarDays(new Date(), parseISO(lastFedDate));
}

/**
 * Health status thresholds (from design system / PRD):
 * 0-5: healthy (normal 3-5 day feeding range)
 * 6: feed-today (approaching limit)
 * 7: feed-now (at safe limit)
 * 8+: needs-attention (may be weakened)
 */
export function getHealthStatus(daysSince: number): HealthStatus {
  if (daysSince <= 5) return 'healthy';
  if (daysSince === 6) return 'feed-today';
  if (daysSince === 7) return 'feed-now';
  return 'needs-attention';
}

/**
 * Human-readable relative time ("2 days ago", "just now").
 */
export function formatRelativeTime(isoDate: string): string {
  return formatDistanceToNow(parseISO(isoDate), { addSuffix: true });
}

/**
 * Starter age in days since creation.
 */
export function getStarterAgeDays(createdDate: string): number {
  return differenceInCalendarDays(new Date(), parseISO(createdDate));
}

/**
 * Average days between feedings.
 */
export function getAverageFeedingInterval(feedingDates: string[]): number {
  if (feedingDates.length < 2) return 0;
  const sorted = feedingDates
    .map((d) => parseISO(d).getTime())
    .sort((a, b) => a - b);
  let totalDays = 0;
  for (let i = 1; i < sorted.length; i++) {
    totalDays += (sorted[i] - sorted[i - 1]) / (1000 * 60 * 60 * 24);
  }
  return Math.round((totalDays / (sorted.length - 1)) * 10) / 10;
}

/**
 * Format a date for display in feeding history.
 */
export function formatFeedingDate(isoDate: string): string {
  const date = parseISO(isoDate);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Returns the current day of the wizard (1-4) based on the starter creation date.
 */
export function getWizardDay(startDate: string): number {
  const days = differenceInCalendarDays(new Date(), parseISO(startDate));
  return Math.min(days + 1, 4); // Day 1 on creation day, max Day 4
}
